import React, { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'

import { uuid4 } from '@sentry/utils'
import { useDropzone } from 'react-dropzone'
import { useForm } from 'react-hook-form'

import { Plane } from '@style-port/roov-ui-components-react'
import { ErrorTxt, Icon, LineLimitTxt, SimplePlane, Txt } from 'src/components/atoms'
import { CommonContentAssetDropArea, CommonContentUploadedBase } from 'src/components/molecules'
import { CommonContentAssetInformation, CommonContentAssetMenu } from 'src/components/organisms'

import { yupResolver } from 'src/customYup'
import {
  useCommonContentAsset,
  useCommonContentPageLoading,
  useGetCommonContentPages,
  useToast,
} from 'src/hooks'
import { isValidURL } from 'src/libs/helper'
import {
  CommonContentAssetEntity,
  CommonContentAssetFormEntity,
  CommonContentAssetFormEntitySchema,
} from 'src/types'

type Props = {
  asset: CommonContentAssetEntity
  displayContentNumber: number
  className?: string
  canEdit?: boolean
  canDelete?: boolean
}

export const CommonContentUploadedAsset: FC<Props> = ({
  asset,
  displayContentNumber,
  className,
  canEdit,
  canDelete,
}) => {
  // URLを入力するinput用のid、refを使う事でUIに影響されない一意の値にする
  const urlInputIdReference = useRef(uuid4())
  const nameInputIdReference = useRef(uuid4())

  const [isUploading, setIsUploading] = useState(false)
  const [showUrlFormatErrorMessage, setUrlFormatErrorMessage] = useState(false)

  const { updateCommonContentAsset, toCommonContentAssetFormEntity, uploadAssetFile } =
    useCommonContentAsset()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { refetch } = useGetCommonContentPages()
  const { showCommonContentPageLoading, hideCommonContentPageLoading } =
    useCommonContentPageLoading()

  const inputFileReference = useRef<HTMLInputElement>(null)

  const { register, watch, setValue } = useForm<CommonContentAssetFormEntity>({
    resolver: yupResolver(CommonContentAssetFormEntitySchema),
    defaultValues: toCommonContentAssetFormEntity(asset),
  })

  const handleUrlOnSave = async () => {
    const url = watch('url')

    // アップロード中後続処理が意図せず実行されると、タイトルがURLでない旨のエラーメッセージが表示され続けてしまう
    if (isUploading) {
      return
    }

    if (url && !isValidURL(url)) {
      setUrlFormatErrorMessage(true)
      return
    }

    setUrlFormatErrorMessage(false)

    // 変更がない場合は何もしない
    // ただしURLのフォーマットが誤っていた場合は、UI上の見せ方の影響で変更がなくても保存する
    if (!showUrlFormatErrorMessage && url === asset.externalLink) {
      return
    }

    showCommonContentPageLoading()

    try {
      const formEntity = { ...toCommonContentAssetFormEntity(asset), url }
      await updateCommonContentAsset({
        commonContentPageId: asset.pageId,
        assetId: asset.id,
        formEntity,
      })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      displayErrorTextToast('保存に失敗しました')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const handleOnUrlInputEnterKeyDown = async () => {
    // querySelectorの場合頭が数値で始まるとエラーを起こす場合があるため getElementById で回避
    // eslint-disable-next-line unicorn/prefer-query-selector
    const inputElement = document.getElementById(
      urlInputIdReference.current
    ) as HTMLInputElement | null
    // blurを外す事で自動的に保存がされる
    inputElement?.blur()
  }

  const handleNameOnBlur = async () => {
    const name = watch('name')

    // 変更がない場合は何もしない
    if (name === asset.name) {
      return
    }

    showCommonContentPageLoading()

    try {
      const formEntity = { ...toCommonContentAssetFormEntity(asset), name }
      await updateCommonContentAsset({
        commonContentPageId: asset.pageId,
        assetId: asset.id,
        formEntity,
      })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      displayErrorTextToast('保存に失敗しました')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const handleNameInputOnKeyDown = async (event: KeyboardEvent) => {
    // Enter以外はなにもしない
    if (event.key !== 'Enter') {
      return
    }

    // querySelectorの場合頭が数値で始まるとエラーを起こす場合があるため getElementById で回避
    // eslint-disable-next-line unicorn/prefer-query-selector
    const textAreaElement = document.getElementById(
      nameInputIdReference.current
    ) as HTMLTextAreaElement | null
    // blurを外す事で自動的に保存がされる
    textAreaElement?.blur()
  }

  const handleOnUpload = async () => {
    // buttonをクリックしたらinputを選択した事に
    inputFileReference.current?.click()
  }

  const fileUpload = async (file: File) => {
    setIsUploading(true)
    showCommonContentPageLoading()

    // 先にアセットタイトル部分を入力した状態でファイルをアップロードすると、タイトルの編集が出来ずURLフォーマットエラーの文字が出続けてしまう
    setUrlFormatErrorMessage(false)

    try {
      await uploadAssetFile({ asset, file })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      displayErrorTextToast('アップロードに失敗しました')
    } finally {
      setIsUploading(false)
      hideCommonContentPageLoading()
    }
  }

  const onFileChangeCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target

    if (!files || files.length === 0) {
      return
    }

    if (files.length > 1) {
      displayErrorTextToast('単体のファイルのみアップロードが可能です')
      return
    }

    await fileUpload(files[0])
  }

  const onDrop = async (acceptedFiles: any) => {
    if ((acceptedFiles as FileList).length > 1) {
      displayErrorTextToast('単体のファイルのみアップロードが可能です')
      return
    }

    await fileUpload(acceptedFiles[0])
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // noClickを設定する事で子要素の メニューのクリックの際などには反応させないようにする
    noClick: true,
  })

  useEffect(() => {
    setValue('name', asset.name)
  }, [asset, setValue])

  return (
    <div className={className}>
      <div className="tw-outline-none" {...getRootProps()}>
        <CommonContentUploadedBase
          contentNumber={displayContentNumber}
          asset={asset}
          isUploading={isUploading}
          onClickUpload={handleOnUpload}
          className="tw-relative"
          canEdit={canEdit}
        >
          <div className="tw-flex tw-w-full tw-h-full">
            <div className="tw-h-fit tw-w-full">
              {asset.contentType === 'empty' ? (
                <Txt className="tw-text-standardText-secondary" sizeType="s" data-no-dnd>
                  または
                </Txt>
              ) : (
                <Plane
                  placeholder="コンテンツ名称"
                  // Planeが最初に高さ持つためには指定が必要
                  defaultValue={watch('name')}
                  {...register('name')}
                  onBlur={handleNameOnBlur}
                  onKeyDown={handleNameInputOnKeyDown}
                  // refはreact-hook-formで使用しているためidで無理矢理要素を取得出来る状態にする
                  id={nameInputIdReference.current}
                  data-no-dnd
                  disabled={showUrlFormatErrorMessage || !canEdit}
                  className="tw-max-h-[2.9rem]"
                />
              )}
              {(asset.contentType === 'empty' || asset.contentType === 'link') && (
                <SimplePlane
                  placeholder="共有したいURLを入力"
                  {...register('url')}
                  onBlur={handleUrlOnSave}
                  onEnterKeyDown={handleOnUrlInputEnterKeyDown}
                  className="tw-mt-s-4 tw-w-full"
                  // refはreact-hook-formで使用しているためidで無理矢理要素を取得出来る状態にする
                  id={urlInputIdReference.current}
                  disabled={!canEdit}
                  data-no-dnd
                />
              )}
              {asset.contentType !== 'empty' && asset.contentType !== 'link' && (
                <div className="tw-mt-s-1 tw-pb-s-1">
                  <div className="tw-flex tw-items-center">
                    <Icon
                      size="--r0"
                      path="file/soft/line"
                      className="tw-text-standardText-disable"
                    />
                    <div data-no-dnd>
                      <LineLimitTxt
                        sizeType="xs"
                        maxLineLimit={1}
                        text={asset.fileName ?? ''}
                        className="tw-text-standardText-secondary tw-ml-s-4"
                      />
                    </div>
                  </div>
                </div>
              )}
              {showUrlFormatErrorMessage && (
                <ErrorTxt
                  text="URLのフォーマットが正しくありません"
                  showIcon
                  className="tw-mt-s-3"
                />
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-w-fit tw-shrink">
              <CommonContentAssetMenu
                asset={asset}
                onReUpload={handleOnUpload}
                canEdit={canEdit}
                canDelete={canDelete}
              />
              {asset.contentType !== 'empty' && <CommonContentAssetInformation asset={asset} />}
            </div>
          </div>

          <input
            {...getInputProps()}
            type="file"
            hidden
            ref={inputFileReference}
            onChange={
              () => {} /* NOTE: onChangeを定義しないとlocalの開発時に onChangeCapture が2回呼ばれてしまうという謎現状が起きる */
            }
            onChangeCapture={onFileChangeCapture}
            accept=".png,.jpg,.gif,.pdf,.mp4"
          />

          {isDragActive && (
            <CommonContentAssetDropArea className="tw-absolute tw-w-full tw-h-full tw-top-0 tw-left-0 tw-z-50" />
          )}
        </CommonContentUploadedBase>
      </div>
    </div>
  )
}
